import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { usd } from "@/components/InvoiceModal";
import { Printer, HeartHandshake } from "lucide-react";

const STATUS = {
  draft: { bg: "bg-slate-100", text: "text-slate-600", label: "Draft" },
  sent: { bg: "bg-amber-50", text: "text-amber-700", label: "Sent" },
  paid: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Paid" },
};

export default function InvoiceViewModal({ invoice, open, onOpenChange }) {
  if (!invoice) return null;
  const s = STATUS[invoice.status] || STATUS.draft;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="invoice-view-modal">
        <div className="print-area">
          <div className="flex items-start justify-between border-b border-slate-200 pb-5">
            <div>
              <div className="flex items-center gap-2 text-[#1e3a8a]">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#1e3a8a]">
                  <HeartHandshake className="h-5 w-5 text-white" />
                </div>
                <span className="font-display text-xl font-bold">Dual Care</span>
              </div>
              <p className="mt-2 text-xs text-slate-500">Dual Care Platform Services, Inc.</p>
            </div>
            <div className="text-right">
              <p className="font-display text-lg font-bold text-slate-900">INVOICE</p>
              <p className="font-mono text-sm text-slate-600">{invoice.invoice_number}</p>
              <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-6 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bill To</p>
              <p className="mt-1 font-semibold text-slate-800">{invoice.county_name}</p>
              {invoice.county_contact && <p className="text-slate-600">{invoice.county_contact}</p>}
              {invoice.county_email && <p className="text-slate-600">{invoice.county_email}</p>}
              {invoice.county_address && <p className="text-slate-600">{invoice.county_address}</p>}
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-600"><span className="text-slate-400">Issue date:</span> {invoice.issue_date}</p>
              {invoice.due_date && <p className="text-sm text-slate-600"><span className="text-slate-400">Due date:</span> {invoice.due_date}</p>}
              <p className="text-sm text-slate-600"><span className="text-slate-400">Period:</span> {invoice.period_label}</p>
            </div>
          </div>

          <table className="mt-6 w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="pb-2 font-semibold">Description</th>
                <th className="pb-2 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoice.line_items.map((it, i) => (
                <tr key={i}>
                  <td className="py-3 text-slate-700">{it.description}</td>
                  <td className="py-3 text-right font-medium text-slate-800">{usd(it.amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200">
                <td className="pt-3 text-right font-display font-bold text-slate-900">Total Due</td>
                <td className="pt-3 text-right font-display text-lg font-bold text-[#1e3a8a]">{usd(invoice.total)}</td>
              </tr>
            </tfoot>
          </table>

          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
            <span className="font-semibold text-slate-600">Usage this period:</span>{" "}
            {invoice.usage?.parents ?? 0} parents · {invoice.usage?.completed_visits ?? 0} completed visits · {invoice.usage?.reports ?? 0} reports
          </div>

          {invoice.notes && <p className="mt-4 text-sm text-slate-600"><span className="font-semibold">Notes:</span> {invoice.notes}</p>}

          <p className="mt-6 border-t border-slate-200 pt-4 text-center text-xs text-slate-400">
            Thank you. Please remit payment by the due date. Ref {invoice.invoice_number}.
          </p>
        </div>

        <div className="no-print mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-invoice-btn">Close</Button>
          <Button onClick={() => window.print()} data-testid="print-invoice-btn" className="bg-[#0d9488] hover:bg-[#115e59]">
            <Printer className="mr-2 h-4 w-4" /> Print / Save PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
